import { type Reference, type StoreObject } from '@apollo/client/core';
import { useApolloClient, useQuery } from '@vue/apollo-composable';
import { computed, ref, watch } from 'vue';

import { useApolloQueryWithPreviousData } from '@/shared/api';
import {
  GetMessengerConversationGroupDocument,
  MessengerDialogSection,
  MessengerMessageFragmentDoc,
  GetMessengerConversationGroupsDocument,
  type GetMessengerConversationGroupsQuery,
} from '@shared/api/graphql/gql/graphql';

import type { MessengerConversation, MessengerConversationGroup, MessengerMessage } from './types';

const PER_PAGE = 20;

type ConversationGroupsMeta = GetMessengerConversationGroupsQuery['messengerConversationGroups']['meta'];

/** Значение поля Query.messengerConversationGroups в кэше (группы — inline, keyFields:false). */
interface GroupsFieldValue {
  groups?: readonly StoreObject[];
  meta?: unknown;
}

/**
 * Composable списка групп диалогов (GraphQL/Apollo).
 *
 * Источник правды — Apollo-кэш: список объединён field-policy merge (R5, кастомная
 * постраничная пагинация с дедупом по client_id), `conversationGroups` —
 * `computed` поверх result (без ref-зеркала). Оптимистичные WS-правки
 * (updateUnreadCount/updateLastMessage) пишут напрямую в кэш через `cache.modify`
 * (в обход merge — список групп может меняться в месте). Публичная форма
 * (`computed`-совместимый список) сохранена — MessengerPage не меняется.
 */
export const useMessengerConversations = () => {
  const { resolveClient } = useApolloClient();
  const searchQuery = ref('');
  const loadedPage = ref(1);
  // Дефолт — «Открытые» (все status=open, включая ожидающие ответа): оператор сразу
  // видит новые входящие. «Неответенные» — фокус-подмножество, «Все» — надмножество.
  const sectionFilter = ref(MessengerDialogSection.Open);

  // page фиксирован на 1: докачка страниц идёт через fetchMore, который сливает
  // их в кэш merge-политикой (R5), поэтому смена search/section даёт свежий список.
  const variables = computed(() => ({
    search: searchQuery.value.trim() || null,
    page: 1,
    perPage: PER_PAGE,
    section: sectionFilter.value,
  }));

  const {
    result,
    loading,
    error: queryError,
    fetchMore,
    refetch,
  } = useQuery(GetMessengerConversationGroupsDocument, variables, {
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const { displayData } = useApolloQueryWithPreviousData(result, loading);

  const meta = computed<ConversationGroupsMeta | null>(
    () => displayData.value?.messengerConversationGroups.meta ?? null,
  );

  watch(
    () => meta.value?.current_page,
    (page) => {
      if (typeof page === 'number' && page >= 1) {
        loadedPage.value = page;
      }
    },
    { immediate: true },
  );

  // Кэш — источник правды: computed поверх result (без ref-зеркала).
  const conversationGroups = computed<MessengerConversationGroup[]>(
    () => displayData.value?.messengerConversationGroups.groups ?? [],
  );

  const totalUnreadCount = computed(() => conversationGroups.value.reduce((sum, group) => sum + group.unread_count, 0));
  const total = computed(() => meta.value?.total ?? 0);
  const currentPage = computed(() => loadedPage.value);
  const hasMore = computed(() => meta.value?.has_more ?? false);
  const isLoading = computed(() => loading.value && conversationGroups.value.length === 0);
  const isLoadingMore = ref(false);
  const error = computed(() => (queryError.value ? String(queryError.value) : null));

  /**
   * Загрузить следующую страницу (докачка через field-policy merge R5).
   */
  const loadMore = async (): Promise<void> => {
    if (!hasMore.value || isLoadingMore.value || loading.value) {
      return;
    }

    isLoadingMore.value = true;
    try {
      const nextPage = loadedPage.value + 1;
      // Конкатенацию/дедуп делает field-policy merge (R5); ручной updateQuery не нужен.
      await fetchMore({
        variables: {
          search: searchQuery.value.trim() || null,
          page: nextPage,
          perPage: PER_PAGE,
          section: sectionFilter.value,
        },
      });
      loadedPage.value = nextPage;
    } finally {
      isLoadingMore.value = false;
    }
  };

  /**
   * Поиск по разговорам (реактивная переменная запроса).
   */
  const search = (query: string): void => {
    searchQuery.value = query;
    loadedPage.value = 1;
  };

  /**
   * Сменить активный раздел (сбрасывает пагинацию; кэш раздельный по section).
   */
  const setSection = (section: MessengerDialogSection): void => {
    sectionFilter.value = section;
    loadedPage.value = 1;
  };

  /**
   * Перезагрузить список (с опциональным сбросом поиска).
   */
  const fetch = async (reset: boolean = true): Promise<void> => {
    if (reset) {
      searchQuery.value = '';
      loadedPage.value = 1;
    }
    await refetch();
  };

  const getGroupByClientId = (clientId: string | null): MessengerConversationGroup | undefined => {
    return conversationGroups.value.find((group) => group.client_id === clientId);
  };

  const getGroupByConversationId = (conversationId: string): MessengerConversationGroup | null => {
    for (const group of conversationGroups.value) {
      if (group.conversations.some((conversation: MessengerConversation) => conversation.id === conversationId)) {
        return group;
      }
    }

    return null;
  };

  /**
   * Точечно догрузить группу по id conversation (вне пагинации и секции списка).
   * Нужен для deeplink и удержания открытого окна: когда группы нет в загруженной
   * странице списка, страница берёт её отсюда. `network-only` пишет нормализованные
   * сущности (dialog/conversation/client/message) в кэш — снапшот окна остаётся
   * реактивным к их последующим патчам. Промах (чужой тенант / нет диалога) → `null`.
   */
  const fetchGroupByConversationId = async (conversationId: string): Promise<MessengerConversationGroup | null> => {
    const { data } = await resolveClient().query({
      query: GetMessengerConversationGroupDocument,
      variables: { conversationId },
      fetchPolicy: 'network-only',
    });

    return data.messengerConversationGroup ?? null;
  };

  /**
   * Оптимистично обновить счётчик непрочитанных для группы — через `cache.modify`
   * (напрямую в поле, в обход merge). Применяется ко всем search-вариациям поля:
   * где группы нет — no-op.
   */
  const updateUnreadCount = (clientId: string | null, count: number): void => {
    resolveClient().cache.modify({
      fields: {
        messengerConversationGroups(existing, { readField }) {
          const value = existing as GroupsFieldValue | undefined;
          if (!value?.groups) {
            return existing;
          }

          let changed = false;
          const groups: StoreObject[] = [];
          for (const group of value.groups) {
            if (readField('client_id', group) === clientId) {
              changed = true;
              groups.push({ ...group, unread_count: count });
            } else {
              groups.push(group);
            }
          }

          return changed ? { ...value, groups } : existing;
        },
      },
    });
  };

  /**
   * Оптимистично проставить последнее сообщение группе и поднять её вверх — через
   * `cache.modify`. Сообщение записывается в кэш (нормализуется по id) и
   * подставляется ссылкой в `last_message` группы.
   */
  const updateLastMessage = (clientId: string | null, message: MessengerMessage | null): void => {
    if (!message) {
      return;
    }

    const { cache } = resolveClient();
    const messageRef = cache.writeFragment({
      fragment: MessengerMessageFragmentDoc,
      fragmentName: 'MessengerMessage',
      data: message,
    });
    if (!messageRef) {
      return;
    }

    cache.modify({
      fields: {
        messengerConversationGroups(existing, { readField }) {
          const value = existing as GroupsFieldValue | undefined;
          if (!value?.groups) {
            return existing;
          }

          const index = value.groups.findIndex((group) => readField('client_id', group) === clientId);
          if (index === -1) {
            return existing;
          }

          const target = value.groups[index];
          if (!target) {
            return existing;
          }

          const updated: StoreObject = {
            ...target,
            last_message: messageRef,
            last_message_at: message.created_at,
          };
          const rest = value.groups.filter((_, i) => i !== index);

          return { ...value, groups: [updated, ...rest] };
        },
      },
    });
  };

  /**
   * Оптимистично убрать группу из разделов OPEN/UNANSWERED после закрытия её диалога
   * («Вопрос решён»): диалог перешёл в resolved и больше не член этих разделов, но
   * серверное членство списка на клиенте само не пересчитывается — без этого карточка
   * висела бы в «Открытых» до перезагрузки. Группа сопоставляется по id рабочего
   * диалога; разделы ALL/RESOLVED не трогаем — их восстановит cache-and-network при
   * следующем переключении (там закрытый диалог, наоборот, должен присутствовать).
   */
  const removeGroupByDialogId = (dialogId: string): void => {
    resolveClient().cache.modify({
      fields: {
        messengerConversationGroups(existing, { readField, storeFieldName }) {
          const value = existing as GroupsFieldValue | undefined;
          if (!value?.groups) {
            return existing;
          }
          // Только разделы, из которых закрытый диалог обязан исчезнуть (keyArgs
          // сериализует section в storeFieldName).
          if (!storeFieldName.includes('"section":"OPEN"') && !storeFieldName.includes('"section":"UNANSWERED"')) {
            return existing;
          }

          const groups = value.groups.filter((group) => {
            const dialogRef = readField<Reference>('dialog', group);
            return !dialogRef || readField('id', dialogRef) !== dialogId;
          });

          return groups.length === value.groups.length ? existing : { ...value, groups };
        },
      },
    });
  };

  /**
   * Перечитать список (например, при новом чате вне списка).
   */
  const invalidateConversations = (): void => {
    loadedPage.value = 1;
    void refetch();
  };

  return {
    // Данные
    conversationGroups,
    totalUnreadCount,
    total,
    currentPage,
    searchQuery,
    section: sectionFilter,

    // Состояние
    isLoading,
    isLoadingMore,
    hasMore,
    error,

    // Методы
    fetch,
    loadMore,
    search,
    setSection,
    getGroupByClientId,
    getGroupByConversationId,
    fetchGroupByConversationId,
    updateUnreadCount,
    updateLastMessage,
    removeGroupByDialogId,
    invalidateConversations,
  };
};
